import { useParams, Link, Navigate } from "@/lib/router-compat";
import { jsonLd } from "@/lib/seoHelpers";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import PageLoading from "@/components/PageLoading";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Star, ExternalLink, ShoppingCart, ChevronRight, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { productHubs } from "@/lib/productHubConfig";
import { PriceDisplay } from "@/components/PriceDisplay";
import { MatchingReviewCTA } from "@/components/MatchingReviewCTA";
import { buildProductSchema, dedupeAffiliatesByRetailer } from "@/lib/offerDefaults";

const BASE_URL = "https://www.garden-review.co.uk";

interface Props {
  hubSlug: string;
}

const GenericProduct = ({ hubSlug }: Props) => {
  // Under the migrated /$slug/$productSlug route the product slug arrives as
  // `productSlug`; dedicated static routes still deliver it as `slug`.
  const params = useParams<{ slug?: string; productSlug?: string }>();
  const slug = params.productSlug ?? params.slug;
  const config = productHubs[hubSlug];

  const { data: product, isLoading } = useQuery({
    queryKey: ["product", hubSlug, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .eq("product_type", hubSlug)
        .eq("slug", slug!)
        .eq("status", "published")
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!slug && !!config,
  });

  const { data: affiliatesRaw = [] } = useQuery({
    queryKey: ["product-affiliates", product?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_affiliates")
        .select("*")
        .eq("product_id", product!.id)
        .order("price_gbp", { ascending: true, nullsFirst: false });
      if (error) throw error;
      return data;
    },
    enabled: !!product?.id,
  });

  if (!config) return <Navigate to="/" replace />;

  if (isLoading) {
    return <PageLoading />;
  }

  if (!product) {
    return (
      <>
        <Header />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{config.singular} Not Found</h1>
            <Link to={`/${config.slug}`}><Button>Browse All {config.title}</Button></Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const title = `${product.brand} ${product.model} — Specs, Price & Where to Buy in the UK`;
  const rawDesc = (product.description || "").trim();
  const metaDescription = rawDesc
    ? (rawDesc.length > 155 ? rawDesc.slice(0, 152).replace(/\s+\S*$/, "") + "…" : rawDesc)
    : `${product.brand} ${product.model} — compare UK prices and specs.`;
  const pageUrl = `${BASE_URL}/${config.slug}/${product.slug}`;
  const affiliates = dedupeAffiliatesByRetailer(affiliatesRaw as any[]);
  const cheapest = affiliates.length > 0 ? affiliates[0] : null;
  const displayPrice = cheapest?.price_gbp || product.price_gbp;

  const productSchema = buildProductSchema(product as any, affiliates as any, { fallbackDescription: metaDescription });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: BASE_URL },
      { "@type": "ListItem", position: 2, name: config.title, item: `${BASE_URL}/${config.slug}` },
      { "@type": "ListItem", position: 3, name: `${product.brand} ${product.model}`, item: pageUrl },
    ],
  };

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={metaDescription} />
        {/* canonical is emitted server-side by the route head() */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        {product.image_url && <meta property="og:image" content={product.image_url} />}
        {product.image_url && <meta property="og:image:alt" content={`${product.brand} ${product.model} ${config.singular}`} />}
        <script type="application/ld+json">{jsonLd(productSchema)}</script>
        <script type="application/ld+json">{jsonLd(breadcrumbSchema)}</script>
      </Helmet>

      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8">
        <nav className="flex items-center gap-1 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-foreground">Home</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to={`/${config.slug}`} className="hover:text-foreground">{config.title}</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">{product.brand} {product.model}</span>
        </nav>

        <div className="md:flex gap-8 mb-10">
          {product.image_url && (
            <div className="md:w-96 shrink-0 mb-6 md:mb-0">
              <div className="bg-muted rounded-xl overflow-hidden aspect-square flex items-center justify-center">
                <img
                  src={product.image_url}
                  alt={`${product.brand} ${product.model} ${config.singular}`}
                  className="w-full h-full object-contain p-4"
                />
              </div>
            </div>
          )}
          <div className="flex-1">
            <p className="text-sm text-muted-foreground uppercase tracking-wide">{product.brand}</p>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-3">{product.brand} {product.model}</h1>
            {product.rating && (
              <div className="flex items-center gap-2 mb-3">
                <div className="flex items-center gap-1 bg-accent/10 text-accent px-3 py-1 rounded-full">
                  <Star className="h-4 w-4 fill-current" />
                  <span className="font-bold">{product.rating}/5</span>
                </div>
              </div>
            )}
            {product.best_for && <Badge variant="secondary" className="mb-4 text-sm">{product.best_for}</Badge>}
            <div className="flex flex-wrap gap-2 mb-4">
              {product.power_type && <Badge variant="outline" className="capitalize">{product.power_type}</Badge>}
            </div>
            {displayPrice && <p className="text-3xl font-bold text-foreground mb-1">From £{displayPrice.toLocaleString()}</p>}
            {product.price_range && <p className="text-sm text-muted-foreground mb-4 capitalize">{product.price_range} price range</p>}
            {cheapest ? (
              <a href={cheapest.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow">
                <Button size="lg" className="gap-2 text-base px-8">
                  <ShoppingCart className="h-5 w-5" /> Buy from {cheapest.retailer_name}
                  {cheapest.price_gbp ? ` — £${cheapest.price_gbp.toLocaleString()}` : ""}
                </Button>
              </a>
            ) : product.affiliate_link ? (
              <a href={product.affiliate_link} target="_blank" rel="noopener noreferrer sponsored nofollow">
                <Button size="lg" className="gap-2 text-base px-8">
                  <ShoppingCart className="h-5 w-5" /> Buy from {product.affiliate_source || "Retailer"}
                  {product.price_gbp ? ` — £${product.price_gbp.toLocaleString()}` : ""}
                </Button>
              </a>
            ) : null}
          </div>
        </div>

        <MatchingReviewCTA brand={product.brand} model={product.model} externalUrl={product.expert_review_url} />

        {affiliates.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Compare Prices from {affiliates.length} UK {affiliates.length === 1 ? "Retailer" : "Retailers"}
            </h2>
            <div className="grid gap-3">
              {affiliates.map((aff: any, i: number) => (
                <a
                  key={aff.id}
                  href={aff.affiliate_link}
                  target="_blank"
                  rel="noopener noreferrer sponsored nofollow"
                  className={cn(
                    "flex items-center justify-between p-4 rounded-lg border text-sm transition-all hover:shadow-md",
                    aff.is_primary
                      ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                      : i === 0
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:border-primary/30"
                  )}
                >
                  <span className="font-semibold text-base flex items-center gap-2">
                    {aff.retailer_name}
                    {i === 0 && (
                      <Badge className="text-[10px] py-0 px-1.5 bg-primary-foreground/20 text-primary-foreground border-primary-foreground/30">
                        Cheapest
                      </Badge>
                    )}
                  </span>
                  <span className="flex items-center gap-3">
                    <PriceDisplay price={aff.price_gbp} previousPrice={aff.previous_price_gbp} availability={aff.availability} lastChecked={aff.price_last_checked} />
                    <ExternalLink className={cn("h-4 w-4", aff.is_primary ? "text-primary-foreground/70" : "text-muted-foreground")} />
                  </span>
                </a>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-3">As an affiliate partner, we may earn from qualifying purchases.</p>
          </section>
        )}

        {product.description && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-3">Overview</h2>
            <p className="text-muted-foreground leading-relaxed">{product.description}</p>
          </section>
        )}


        {(product.key_features as string[])?.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-bold text-foreground mb-4">Key Features</h2>
            <ul className="grid md:grid-cols-2 gap-3">
              {(product.key_features as string[]).map((feature, i) => (
                <li key={i} className="flex items-start gap-2 text-sm bg-muted/50 rounded-lg p-3">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>{feature}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="text-center border-t pt-8">
          <Link to={`/${config.slug}`}>
            <Button variant="outline" className="gap-2"><ChevronRight className="h-4 w-4 rotate-180" /> Back to {config.title}</Button>
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default GenericProduct;
